import type { ApiClient } from '../client';
import type {
  Flashcard,
  DrillSession,
  DrillAudio,
  DrillAttemptAudio,
  DrillAttemptRnAudioFile,
  DrillAttemptResult,
  PaginatedResponse,
  GameSession,
  GameAnswerResult,
  LearningProgress,
  LearningProgressQuery,
  GameType,
  ArcadeRunCreateOptions,
  ArcadeRun,
  ArcadeAnswerResult,
  ArcadeRunState,
  ArcadeCompleteResult,
  ArcadeAbandonResult,
  ArcadePersonalBestsResponse,
  GhostRunCreateOptions,
  GhostRun,
  GhostTraceResponse,
  GhostTracesListResponse,
  ListenPickAudio,
} from '../types';

/**
 * Narrow RN FormData surface: web FormData.append only accepts Blob|string,
 * but React Native FormData also accepts `{ uri, name, type }` descriptors.
 * Keep the cast confined to this boundary after runtime validation.
 */
type RnFormDataValue = string | Blob | DrillAttemptRnAudioFile;
interface RnFormDataBoundary {
  append(name: string, value: RnFormDataValue): void;
}

function isBlobOrFile(value: unknown): value is Blob {
  return typeof Blob !== 'undefined' && value instanceof Blob;
}

function isDrillAttemptRnAudioFile(value: unknown): value is DrillAttemptRnAudioFile {
  if (value === null || typeof value !== 'object' || isBlobOrFile(value)) {
    return false;
  }
  const rec = value as Record<string, unknown>;
  return (
    typeof rec['uri'] === 'string' &&
    (rec['uri'] as string).length > 0 &&
    typeof rec['name'] === 'string' &&
    (rec['name'] as string).length > 0 &&
    typeof rec['type'] === 'string' &&
    (rec['type'] as string).length > 0
  );
}

function appendDrillAttemptAudio(formData: FormData, audio: DrillAttemptAudio): void {
  if (isBlobOrFile(audio)) {
    formData.append('audio', audio);
    return;
  }
  if (isDrillAttemptRnAudioFile(audio)) {
    (formData as unknown as RnFormDataBoundary).append('audio', audio);
    return;
  }
  throw new TypeError(
    'submitDrillAttempt: audio must be a Blob/File or a React Native { uri, name, type } descriptor'
  );
}

export interface LearningApi {
  getDueFlashcards(): Promise<Flashcard[]>;
  getFlashcards(cursor?: string, limit?: number): Promise<PaginatedResponse<Flashcard>>;
  reviewFlashcard(id: string, quality: 1 | 5): Promise<Flashcard>;
  createDrillSession(limit?: number): Promise<DrillSession>;
  getDrillAudio(itemId: string): Promise<DrillAudio>;
  /**
   * REQ-LEARN-013 / REQ-MOB-019: multipart audio assessment.
   * Field name must be `audio`. Clients must not score locally.
   */
  submitDrillAttempt(itemId: string, audio: DrillAttemptAudio): Promise<DrillAttemptResult>;
  createGameSession(options?: { types?: GameType[]; limit?: number }): Promise<GameSession>;
  answerGameQuestion(sessionId: string, answer: string): Promise<GameAnswerResult>;
  getProgress(query?: LearningProgressQuery): Promise<LearningProgress>;
  /** Arcade: start a run with batched questions (COR-133). */
  createArcadeRun(options: ArcadeRunCreateOptions): Promise<ArcadeRun>;
  /** Arcade: server-authoritative grade; idempotent on questionId. */
  answerArcadeQuestion(
    runId: string,
    body: { questionId: string; answer: string }
  ): Promise<ArcadeAnswerResult>;
  /** Arcade: resume after network blip. */
  getArcadeRun(runId: string): Promise<ArcadeRunState>;
  /** Arcade: recompute score and bank personal best (NR-4). */
  completeArcadeRun(runId: string): Promise<ArcadeCompleteResult>;
  /** Arcade: explicit abandon — does not bank a personal best. */
  abandonArcadeRun(runId: string): Promise<ArcadeAbandonResult>;
  /** Arcade: self personal bests only (AC-06). */
  getArcadePersonalBests(): Promise<ArcadePersonalBestsResponse>;
  /** Resolve an opaque Listen & Pick audio reference through the authenticated API. */
  getListenPickAudio(audioRef: string): Promise<ListenPickAudio>;
  /** Ghost: create a Beat Your Ghost run (FR-4.10). */
  createGhostRun(options: GhostRunCreateOptions): Promise<GhostRun>;
  /** Ghost: get ghost trace for a run's mode/timing. */
  getGhostTrace(runId: string): Promise<GhostTraceResponse>;
  /** Ghost: list all best ghost traces for the user. */
  getGhostBestTraces(): Promise<GhostTracesListResponse>;
}

export function createLearningApi(client: ApiClient): LearningApi {
  return {
    getDueFlashcards: () =>
      client.get<{ items: Flashcard[] }>('/api/learning/flashcards?due=true').then(r => r.items),

    getFlashcards: (cursor?: string, limit = 20) => {
      const params = new URLSearchParams();
      if (cursor) params.set('cursor', cursor);
      params.set('limit', limit.toString());
      return client.get<PaginatedResponse<Flashcard>>(`/api/learning/flashcards?${params}`);
    },

    reviewFlashcard: (id: string, quality: 1 | 5) =>
      client.post<Flashcard>(`/api/learning/flashcards/${encodeURIComponent(id)}/review`, {
        quality,
      }),

    createDrillSession: (limit = 10) =>
      client.post<DrillSession>('/api/learning/drills/sessions', { limit }),

    getDrillAudio: (itemId: string) =>
      client.get<DrillAudio>(`/api/learning/drills/items/${encodeURIComponent(itemId)}/audio`),

    submitDrillAttempt: (itemId: string, audio: DrillAttemptAudio) => {
      try {
        const formData = new FormData();
        appendDrillAttemptAudio(formData, audio);
        return client.post<DrillAttemptResult>(
          `/api/learning/drills/${encodeURIComponent(itemId)}/attempts`,
          formData
        );
      } catch (err) {
        // Keep the contract Promise-returning for malformed audio (sync guard).
        return Promise.reject(err);
      }
    },

    createGameSession: (options = {}) =>
      client.post<GameSession>('/api/learning/games/sessions', options),

    answerGameQuestion: (sessionId, answer) =>
      client.post<GameAnswerResult>(
        `/api/learning/games/sessions/${encodeURIComponent(sessionId)}/answers`,
        { answer }
      ),

    createArcadeRun: options => client.post<ArcadeRun>('/api/learning/arcade/runs', options),

    answerArcadeQuestion: (runId, body) =>
      client.post<ArcadeAnswerResult>(
        `/api/learning/arcade/runs/${encodeURIComponent(runId)}/answers`,
        body
      ),

    getArcadeRun: runId =>
      client.get<ArcadeRunState>(`/api/learning/arcade/runs/${encodeURIComponent(runId)}`),

    completeArcadeRun: runId =>
      client.post<ArcadeCompleteResult>(
        `/api/learning/arcade/runs/${encodeURIComponent(runId)}/complete`,
        {}
      ),

    abandonArcadeRun: runId =>
      client.post<ArcadeAbandonResult>(
        `/api/learning/arcade/runs/${encodeURIComponent(runId)}/abandon`,
        {}
      ),

    getArcadePersonalBests: () =>
      client.get<ArcadePersonalBestsResponse>('/api/learning/arcade/personal-bests'),

    getListenPickAudio: audioRef =>
      client.get<ListenPickAudio>(`/api/learning/games/audio/${encodeURIComponent(audioRef)}`),

    createGhostRun: options => client.post<GhostRun>('/api/learning/ghost/runs', options),

    getGhostTrace: runId =>
      client.get<GhostTraceResponse>(`/api/learning/ghost/traces/${encodeURIComponent(runId)}`),

    getGhostBestTraces: () =>
      client.get<GhostTracesListResponse>('/api/learning/ghost/best-traces'),

    getProgress: (query = {}) => {
      const params = new URLSearchParams();
      if (query.page !== undefined) params.set('page', query.page.toString());
      if (query.limit !== undefined) params.set('limit', query.limit.toString());
      const qs = params.toString();
      return client.get<LearningProgress>(
        qs ? `/api/learning/progress?${qs}` : '/api/learning/progress'
      );
    },
  };
}
